import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface AssetUsageEvent {
  id: string;
  shop_id: string;
  asset_id: string;
  ticket_id: string | null;
  job_id: string | null;
  tech_id: string | null;
  book_minutes: number;
  actual_minutes: number | null;
  source: 'derived_bay' | 'derived_job' | 'manual';
  value_kind: 'bay_enabled' | 'job_attributed';
  notes: string | null;
  created_at: string;
  created_by: string | null;
  // Joined data
  technician?: { id: string; name: string } | null;
}

export interface AssetUsageSummary {
  totalBookMinutes: number;
  totalActualMinutes: number;
  usageCount: number;
  bayEnabledMinutes: number;
  jobAttributedMinutes: number;
}

export function useAssetUsage(assetId: string | null, dateRange?: { start: Date; end: Date }) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['asset-usage', assetId, dateRange?.start?.toISOString(), dateRange?.end?.toISOString()],
    queryFn: async () => {
      if (!assetId || !shopId) return [];
      
      let query = supabase
        .from('shop_asset_usage_events')
        .select(`
          *,
          technician:technicians(id, name)
        `)
        .eq('asset_id', assetId)
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });
      
      if (dateRange) {
        query = query
          .gte('created_at', dateRange.start.toISOString())
          .lt('created_at', dateRange.end.toISOString());
      }
      
      const { data, error } = await query.limit(100);
      
      if (error) throw error;
      return data as AssetUsageEvent[];
    },
    enabled: !!assetId && !!shopId,
  });
}

export function useAssetUsageSummary(assetId: string | null, dateRange?: { start: Date; end: Date }) {
  const { data: events, isLoading, error } = useAssetUsage(assetId, dateRange);

  const summary: AssetUsageSummary = {
    totalBookMinutes: 0,
    totalActualMinutes: 0,
    usageCount: 0,
    bayEnabledMinutes: 0,
    jobAttributedMinutes: 0,
  };

  if (events) {
    for (const event of events) {
      summary.totalBookMinutes += event.book_minutes;
      summary.totalActualMinutes += event.actual_minutes ?? 0;
      summary.usageCount += 1;
      
      if (event.value_kind === 'bay_enabled') {
        summary.bayEnabledMinutes += event.book_minutes;
      } else {
        summary.jobAttributedMinutes += event.book_minutes;
      }
    }
  }

  return { summary, isLoading, error };
}

export function useShopLaborTotals(dateRange: { start: Date; end: Date }) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['shop-labor-totals', shopId, dateRange.start.toISOString(), dateRange.end.toISOString()],
    queryFn: async () => {
      if (!shopId) return null;
      
      const { data, error } = await supabase.rpc('get_shop_labor_totals', {
        p_shop_id: shopId,
        p_start_date: dateRange.start.toISOString(),
        p_end_date: dateRange.end.toISOString(),
      });
      
      if (error) throw error;
      return data as {
        total_book_hours: number;
        total_actual_hours: number;
        generated_labor_value: number;
        ticket_count: number;
      };
    },
    enabled: !!shopId,
  });
}
